import { LoadingOutlined } from '@ant-design/icons';
import { T, useTranslate } from '@tolgee/react';
import { Dropdown, Menu } from 'antd';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { ArrowDownSvg, ArrowUpSvg, DeleteSvg } from '../../assets/icons';
import AnswerSvg from '../../assets/icons/AnswerSvg';
import FolderSvg from '../../assets/icons/FolderSvg';
import MoreSvg from '../../assets/icons/MoreSvg';
import TimerSvg from '../../assets/icons/TimerSvg';
import theme from '../../assets/theme';
import useFolders from '../../hooks/useFolders';
import { useMetrics } from '../../hooks/useMetrics';
import useWorkspaceFolders from '../../hooks/useWorkspaceFolders';
import {
  Folder,
  FolderEditOrCreateDTO,
} from '../../services/TemplateService/Template.type';
import { searchSortOptions } from '../../utils/sortUtils';
import { Input } from '../_atoms/_Input';
import { Button } from '../_atoms/Button';
import { IconWrapper } from '../_atoms/IconWrapper';
import BaseModal from '../_molecules/BaseModal';
import { SortDropdown, SortDropdownOption } from '../_molecules/SortDropdown';
import WhiteSearchInput from '../_molecules/WhiteSearchInput';
import WorkspaceUsers from '../_molecules/WorkspaceUsers';
import { MoveToFolderData } from './MoveToFolder';
import MoveToFolder from './MoveToFolder/MoveToFolder';
import { MoveToTrashModal } from './MoveToTrashModal';

const sortOptions: SortDropdownOption[] = [
  {
    displayName: <T keyName="Newest edited" defaultValue="Newest edited" />,
    value: 'newestModified',
    icon: <TimerSvg />,
  },
  {
    displayName: <T keyName="Oldest edited" defaultValue="Oldest edited" />,
    value: 'oldestModified',
    icon: <TimerSvg />,
  },
  {
    displayName: <T keyName="Alphabetical (A-Z)" defaultValue="Alphabetical (A-Z)" />,
    value: 'alphaAsc',
    icon: <ArrowUpSvg />,
  },
  {
    displayName: <T keyName="Alphabetical (Z-A)" defaultValue="Alphabetical (Z-A)" />,
    value: 'alphaDesc',
    icon: <ArrowDownSvg />,
  },
];

export const sortConditionToQuery = (
  value: SortDropdownOption,
): { orderBy: 'title' | 'modifiedAt'; sortDirection: 'asc' | 'desc' } | undefined => {
  if (value.value === 'alphaAsc') {
    return { orderBy: 'title', sortDirection: 'asc' };
  } else if (value.value === 'alphaDesc') {
    return { orderBy: 'title', sortDirection: 'desc' };
  } else if (value.value === 'newestModified') {
    return { orderBy: 'modifiedAt', sortDirection: 'desc' };
  } else if (value.value === 'oldestModified') {
    return { orderBy: 'modifiedAt', sortDirection: 'asc' };
  }
  return undefined;
};

export const WorkspaceSortingControls = ({
  sortCondition,
  onChange,
  currentFolder,
  enableSearch,
  onSearchChange,
  searchValue,
}: {
  sortCondition?: SortDropdownOption;
  onChange: (val: SortDropdownOption) => void;
  currentFolder?: Folder;
  enableSearch?: boolean;
  onSearchChange?: (val: string) => void;
  searchValue?: string;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const router = useRouter();
  const parentFolderId = router.query.parentFolderId as string;
  const tenantId = router.query.tenantId as string;
  const workspaceId = router.query.workspaceId as string;

  const [isDeletingData, setIsDeletingData] = useState<Folder | undefined>(undefined);
  const [isRenamingFolder, setIsRenamingFolder] = useState<Folder | undefined>(undefined);
  const [isMovingData, setIsMovingData] = useState<MoveToFolderData>();

  const {
    allFolders,
    isDeletingFolder,
    deleteFolder,
    saveFolder,
    deleteFolderVariables,
    isDeleteFolderSuccess,
    resetDeleteFolder,
    isSavingFolder,
  } = useWorkspaceFolders(currentFolder?.id);

  useEffect(() => {
    if (isDeleteFolderSuccess) {
      resetDeleteFolder();
      router.push(
        `/${tenantId}/workspace/${workspaceId}${
          deleteFolderVariables?.parentId
            ? `?parentFolderId=${deleteFolderVariables?.parentId}`
            : ''
        }`,
      );
    }
  }, [
    isDeleteFolderSuccess,
    tenantId,
    parentFolderId,
    router,
    resetDeleteFolder,
    deleteFolderVariables,
    workspaceId,
  ]);

  const handleChange = (val: string) => {
    const selectedOption = sortOptions.find(option => option.value === val);
    if (!selectedOption) return;
    onChange(selectedOption);
  };

  const handleRename = () => {
    if (!isRenamingFolder) return;
    saveFolder({
      folderConfig: {
        ...isRenamingFolder,
        id: isRenamingFolder.id,
      } as FolderEditOrCreateDTO,
      onSuccessFunction: () => {
        setIsRenamingFolder(undefined);
      },
    });
  };

  const handleDeleteFolder = (f: Folder) => {
    deleteFolder(f);
    setIsDeletingData(undefined);
  };

  const renderMenu = (folder: Folder) => (
    <Menu>
      <Menu.Item key={'rename'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.RenameFolder.Start');
            setIsRenamingFolder(folder);
          }}
        >
          <AnswerSvg /> <T keyName="Rename" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'move'}
        style={{ padding: 0 }}
        onClick={() =>
          setIsMovingData({
            id: folder.id,
            type: 'folder',
            isPrivate: folder.isPrivate,
            title: folder.title,
            parentId: folder.parentId,
          })
        }
      >
        <DropdownMenuItemButton>
          <FolderSvg /> <T keyName="Move" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-folder"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item key={'delete'} style={{ padding: 0, color: theme.colors.red }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.DeleteFolder.Start');
            setIsDeletingData(folder);
          }}
        >
          <DeleteSvg /> <T keyName="Move to trash" />
        </DropdownMenuItemButton>
      </Menu.Item>
    </Menu>
  );

  return (
    <SortingControlsContainer>
      <div style={{ flex: 1 }}>
        {enableSearch && (
          <WhiteSearchInput
            style={{ maxWidth: '250px', minWidth: '200px' }}
            value={searchValue}
            onChange={e => onSearchChange && onSearchChange(e)}
            placeholder={t('Search my lessons', 'Search my lessons')}
          />
        )}
        {!enableSearch && (
          <SortDropdown
            value={sortCondition?.value as string | undefined}
            options={sortOptions}
            onSelect={e => handleChange(e as string)}
          />
        )}
      </div>
      {enableSearch && (
        <>
          {searchValue && searchValue?.length > 0 && (
            <SortDropdown
              value={searchSortOptions[0].value}
              options={searchSortOptions}
              onSelect={() => {
                //There is only one option
              }}
            />
          )}
          {!searchValue && (
            <SortDropdown
              value={sortCondition?.value as string | undefined}
              options={sortOptions}
              onSelect={e => handleChange(e as string)}
            />
          )}
          <div style={{ height: '42px', width: 1, background: 'rgb(217,217,217)' }}></div>
        </>
      )}
      <WorkspaceUsers />
      {/* <NewWorkspaceFolder /> */}

      {currentFolder && (
        <Dropdown
          overlay={renderMenu(currentFolder)}
          trigger={['click']}
          placement={'bottomRight'}
        >
          <FolderOptions>
            <IconWrapper height="16px" width="16px">
              <MoreSvg />
            </IconWrapper>
          </FolderOptions>
        </Dropdown>
      )}
      {/* ------------ RENAME FOLDER ----------------- */}
      {isRenamingFolder && (
        <BaseModal
          title={`${t('Rename folder', 'Rename')} ${
            isRenamingFolder?.title ? `"${isRenamingFolder.title}"` : ''
          }          `}
          open
          onCancel={() => setIsRenamingFolder(undefined)}
        >
          <Input
            placeholder={t('New name', 'New name')}
            autoFocus
            maxLength={40}
            value={isRenamingFolder.title || ''}
            disabled={isSavingFolder}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) =>
              setIsRenamingFolder({
                ...isRenamingFolder,
                title: event.target.value ? event.target.value : null,
              })
            }
            onPressEnter={() => handleRename()}
          ></Input>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
              onClick={() => {
                setIsRenamingFolder(undefined);
              }}
            >
              <T keyName="Cancel" />
            </Button>
            <Button disabled={isSavingFolder} onClick={() => handleRename()}>
              {isSavingFolder ? (
                <>
                  <LoadingOutlined /> {t('Renaming', 'Renaming')}
                </>
              ) : (
                t('Rename', 'Rename')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {/* ------------ MOVE FOLDER TO TRASH ----------------- */}
      {isDeletingData && (
        <MoveToTrashModal
          handleDelete={() => handleDeleteFolder(isDeletingData)}
          deleteData={isDeletingData}
          isDeletingData={isDeletingFolder}
          folders={
            allFolders ? allFolders.filter(f => f.parentId === isDeletingData.id) : []
          }
          setDeleteData={setIsDeletingData}
        />
      )}
      {/* ------------ MOVE ----------------- */}
      {!!isMovingData && allFolders && (
        <MoveToFolder
          onCancel={() => setIsMovingData(undefined)}
          data={isMovingData}
          onDone={() => setIsMovingData(undefined)}
        />
      )}
    </SortingControlsContainer>
  );
};
export const WorkspaceFolderSortingControls = ({
  sortCondition,
  onChange,
  currentFolder,
}: {
  sortCondition?: SortDropdownOption;
  onChange: (val: SortDropdownOption) => void;
  currentFolder?: Folder;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const router = useRouter();
  const parentFolderId = router.query.parentFolderId as string;
  const tenantId = router.query.tenantId as string;
  const workspaceId = router.query.workspaceId as string;

  const [isDeletingData, setIsDeletingData] = useState<Folder | undefined>(undefined);
  const [isRenamingFolder, setIsRenamingFolder] = useState<Folder | undefined>(undefined);
  const [isMovingData, setIsMovingData] = useState<MoveToFolderData>();

  const {
    allFolders,
    isDeletingFolder,
    deleteFolder,
    saveFolder,
    deleteFolderVariables,
    isDeleteFolderSuccess,
    resetDeleteFolder,
    isSavingFolder,
  } = useWorkspaceFolders(currentFolder?.id);

  useEffect(() => {
    if (isDeleteFolderSuccess) {
      resetDeleteFolder();
      router.push(
        `/${tenantId}/workspace/${workspaceId}${
          deleteFolderVariables?.parentId
            ? `?parentFolderId=${deleteFolderVariables?.parentId}`
            : ''
        }`,
      );
    }
  }, [
    isDeleteFolderSuccess,
    tenantId,
    parentFolderId,
    router,
    resetDeleteFolder,
    deleteFolderVariables,
    workspaceId,
  ]);

  const handleChange = (val: string) => {
    const selectedOption = sortOptions.find(option => option.value === val);
    if (!selectedOption) return;
    onChange(selectedOption);
  };

  const handleRename = () => {
    if (!isRenamingFolder) return;
    saveFolder({
      folderConfig: {
        ...isRenamingFolder,
        id: isRenamingFolder.id,
      } as FolderEditOrCreateDTO,
      onSuccessFunction: () => {
        setIsRenamingFolder(undefined);
      },
    });
  };

  const handleDeleteFolder = (f: Folder) => {
    deleteFolder(f);
    setIsDeletingData(undefined);
  };

  const renderMenu = (folder: Folder) => (
    <Menu>
      <Menu.Item key={'rename'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.RenameFolder.Start');
            setIsRenamingFolder(folder);
          }}
        >
          <AnswerSvg /> <T keyName="Rename" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'move'}
        style={{ padding: 0 }}
        onClick={() =>
          setIsMovingData({
            id: folder.id,
            type: 'folder',
            isPrivate: folder.isPrivate,
            title: folder.title,
            parentId: folder.parentId,
          })
        }
      >
        <DropdownMenuItemButton>
          <FolderSvg /> <T keyName="Move" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-folder"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item key={'delete'} style={{ padding: 0, color: theme.colors.red }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.DeleteFolder.Start');
            setIsDeletingData(folder);
          }}
        >
          <DeleteSvg /> <T keyName="Move to trash" />
        </DropdownMenuItemButton>
      </Menu.Item>
    </Menu>
  );

  return (
    <SortingControlsContainer>
      <div style={{ flex: 1 }}></div>
      <SortDropdown
        value={sortCondition?.value as string | undefined}
        options={sortOptions}
        onSelect={e => handleChange(e as string)}
      />
      <div style={{ height: '42px', width: 1, background: 'rgb(217,217,217)' }}></div>

      {currentFolder && (
        <Dropdown
          overlay={renderMenu(currentFolder)}
          trigger={['click']}
          placement={'bottomRight'}
        >
          <FolderOptions>
            <IconWrapper height="16px" width="16px">
              <MoreSvg />
            </IconWrapper>
          </FolderOptions>
        </Dropdown>
      )}
      {/* ------------ RENAME FOLDER ----------------- */}
      {isRenamingFolder && (
        <BaseModal
          title={`${t('Rename folder', 'Rename')} ${
            isRenamingFolder?.title ? `"${isRenamingFolder.title}"` : ''
          }          `}
          open
          onCancel={() => setIsRenamingFolder(undefined)}
        >
          <Input
            placeholder={t('New name', 'New name')}
            autoFocus
            maxLength={40}
            value={isRenamingFolder.title || ''}
            disabled={isSavingFolder}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) =>
              setIsRenamingFolder({
                ...isRenamingFolder,
                title: event.target.value ? event.target.value : null,
              })
            }
            onPressEnter={() => handleRename()}
          ></Input>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
              onClick={() => {
                setIsRenamingFolder(undefined);
              }}
            >
              <T keyName="Cancel" />
            </Button>
            <Button disabled={isSavingFolder} onClick={() => handleRename()}>
              {isSavingFolder ? (
                <>
                  <LoadingOutlined /> {t('Renaming')}
                </>
              ) : (
                t('Rename')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {/* ------------ MOVE FOLDER TO TRASH ----------------- */}
      {isDeletingData && (
        <MoveToTrashModal
          handleDelete={() => handleDeleteFolder(isDeletingData)}
          deleteData={isDeletingData}
          isDeletingData={isDeletingFolder}
          folders={
            allFolders ? allFolders.filter(f => f.parentId === isDeletingData.id) : []
          }
          setDeleteData={setIsDeletingData}
        />
      )}
      {/* ------------ MOVE ----------------- */}
      {!!isMovingData && allFolders && (
        <MoveToFolder
          onCancel={() => setIsMovingData(undefined)}
          data={isMovingData}
          onDone={() => setIsMovingData(undefined)}
        />
      )}
    </SortingControlsContainer>
  );
};

export const FolderSortingControls = ({
  sortCondition,
  onChange,
  currentFolder,
}: {
  sortCondition?: SortDropdownOption;
  onChange: (val: SortDropdownOption) => void;
  currentFolder?: Folder;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();

  const router = useRouter();
  const parentFolderId = router.query.parentFolderId as string;
  const tenantId = router.query.tenantId as string;

  const [isDeletingData, setIsDeletingData] = useState<Folder | undefined>(undefined);
  const [isRenamingFolder, setIsRenamingFolder] = useState<Folder | undefined>(undefined);
  const [isMovingData, setIsMovingData] = useState<MoveToFolderData>();

  const {
    allFolders,
    isDeletingFolder,
    deleteFolder,
    saveFolder,
    isDeletingFolderSuccess,
    resetDeleteFolder,
    deleteFolderVariables,
    isSavingFolder,
  } = useFolders(currentFolder?.id);

  useEffect(() => {
    if (isDeletingFolderSuccess) {
      resetDeleteFolder();
      router.push(
        `/${tenantId}/lessons${
          deleteFolderVariables?.parentId
            ? `?parentFolderId=${deleteFolderVariables?.parentId}`
            : ''
        }`,
      );
    }
  }, [
    isDeletingFolderSuccess,
    tenantId,
    parentFolderId,
    router,
    resetDeleteFolder,
    deleteFolderVariables,
  ]);

  const handleChange = (val: string) => {
    const selectedOption = sortOptions.find(option => option.value === val);
    if (!selectedOption) return;
    onChange(selectedOption);
  };

  const handleRename = () => {
    if (!isRenamingFolder) return;
    saveFolder({
      folderConfig: {
        ...isRenamingFolder,
        id: isRenamingFolder.id,
      } as FolderEditOrCreateDTO,
      onSuccessFunction: () => {
        setIsRenamingFolder(undefined);
      },
    });
  };

  const handleDeleteFolder = (f: Folder) => {
    deleteFolder(f);
    setIsDeletingData(undefined);
  };

  const renderMenu = (folder: Folder) => (
    <Menu>
      <Menu.Item key={'rename'} style={{ padding: 0 }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.RenameFolder.Start');
            setIsRenamingFolder(folder);
          }}
        >
          <AnswerSvg /> <T keyName="Rename" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <Menu.Item
        key={'move'}
        style={{ padding: 0 }}
        onClick={() =>
          setIsMovingData({
            id: folder.id,
            type: 'folder',
            isPrivate: folder.isPrivate,
            title: folder.title,
            parentId: folder.parentId,
          })
        }
      >
        <DropdownMenuItemButton>
          <FolderSvg /> <T keyName="Move" />
        </DropdownMenuItemButton>
      </Menu.Item>
      <div
        key="divider-folder"
        style={{ width: '100%', height: 1, backgroundColor: '#C4C4C4' }}
      />
      <Menu.Item key={'delete'} style={{ padding: 0, color: theme.colors.red }}>
        <DropdownMenuItemButton
          onClick={() => {
            metrics.logEvent('Lessons.DeleteFolder.Start');
            setIsDeletingData(folder);
          }}
        >
          <DeleteSvg /> <T keyName="Move to trash" />
        </DropdownMenuItemButton>
      </Menu.Item>
    </Menu>
  );

  return (
    <SortingControlsContainer>
      <div style={{ flex: 1 }}></div>
      <SortDropdown
        value={sortCondition?.value as string | undefined}
        options={sortOptions}
        onSelect={e => handleChange(e as string)}
      />
      <div style={{ height: '42px', width: 1, background: 'rgb(217,217,217)' }}></div>
      {currentFolder && (
        <Dropdown
          overlay={renderMenu(currentFolder)}
          trigger={['click']}
          placement={'bottomRight'}
        >
          <FolderOptions>
            <IconWrapper height="16px" width="16px">
              <MoreSvg />
            </IconWrapper>
          </FolderOptions>
        </Dropdown>
      )}
      {/* ------------ RENAME FOLDER ----------------- */}
      {isRenamingFolder && (
        <BaseModal
          title={`${t('Rename folder', 'Rename')} ${
            isRenamingFolder?.title ? `"${isRenamingFolder.title}"` : ''
          }          `}
          open
          onCancel={() => setIsRenamingFolder(undefined)}
        >
          <Input
            placeholder={t('New name', 'New name')}
            autoFocus
            maxLength={40}
            value={isRenamingFolder.title || ''}
            disabled={isSavingFolder}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) =>
              setIsRenamingFolder({
                ...isRenamingFolder,
                title: event.target.value ? event.target.value : null,
              })
            }
            onPressEnter={() => handleRename()}
          ></Input>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '15px',
            }}
          >
            <Button
              type="tertiary"
              styles={{ border: 'none', color: theme.colors.black, boxShadow: 'none' }}
              onClick={() => {
                setIsRenamingFolder(undefined);
              }}
            >
              <T keyName="Cancel" />
            </Button>
            <Button disabled={isSavingFolder} onClick={() => handleRename()}>
              {isSavingFolder ? (
                <>
                  <LoadingOutlined /> {t('Renaming')}
                </>
              ) : (
                t('Rename')
              )}
            </Button>
          </div>
        </BaseModal>
      )}
      {/* ------------ MOVE FOLDER TO TRASH ----------------- */}
      {isDeletingData && (
        <MoveToTrashModal
          handleDelete={() => handleDeleteFolder(isDeletingData)}
          deleteData={isDeletingData}
          isDeletingData={isDeletingFolder}
          folders={
            allFolders ? allFolders.filter(f => f.parentId === isDeletingData.id) : []
          }
          setDeleteData={setIsDeletingData}
        />
      )}
      {/* ------------ MOVE ----------------- */}
      {!!isMovingData && allFolders && (
        <MoveToFolder
          onCancel={() => setIsMovingData(undefined)}
          data={isMovingData}
          onDone={() => setIsMovingData(undefined)}
        />
      )}
    </SortingControlsContainer>
  );
};

const FolderOptions = styled.div`
  height: 42px;
  display: flex;
  align-items: center;
  background: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: 5px;
  padding: 10px 15px;
  align-items: center;

  span {
    font-weight: bold;
    color: ${theme.colors.black};
    font-size: 16px;
  }
`;

const DropdownMenuItemButton = styled.button`
  background: none;
  border: none;
  padding: 5px 12px;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-weight: bold;
  &:disabled {
    cursor: not-allowed;
  }
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    margin-left: 5px;
    height: 16px;
    width: 16px;
  }
`;

export const SortingControls = ({
  sortCondition,
  onChange,
  enableSearch,
  onSearchChange,
  searchValue,
}: {
  sortCondition?: SortDropdownOption;
  onChange: (val: SortDropdownOption) => void;
  enableSearch?: boolean;
  onSearchChange?: (val: string) => void;
  searchValue?: string;
}) => {
  const { t } = useTranslate();
  const handleChange = (val: string) => {
    const selectedOption = sortOptions.find(option => option.value === val);
    if (!selectedOption) return;
    onChange(selectedOption);
  };

  return (
    <SortingControlsContainer>
      <div style={{ flex: 1 }}>
        {enableSearch && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              maxWidth: '250px',
              minWidth: '200px',
            }}
          >
            <WhiteSearchInput
              style={{ maxWidth: '250px', minWidth: '200px' }}
              value={searchValue}
              onChange={e => onSearchChange && onSearchChange(e)}
              placeholder={t('Search my lessons', 'Search my lessons')}
            />
          </div>
        )}
        {!enableSearch && (
          <SortDropdown
            value={sortCondition?.value as string | undefined}
            options={sortOptions}
            onSelect={e => handleChange(e as string)}
          />
        )}
      </div>
      {enableSearch && (
        <>
          {searchValue && searchValue?.length > 0 && (
            <SortDropdown
              value={searchSortOptions[0].value}
              options={searchSortOptions}
              onSelect={() => {
                //There is only one option - we prevent the user from updating the state.
              }}
            />
          )}
          {!searchValue && (
            <SortDropdown
              value={sortCondition?.value as string | undefined}
              options={sortOptions}
              onSelect={e => handleChange(e as string)}
            />
          )}
        </>
      )}
    </SortingControlsContainer>
  );
};

const SortingControlsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  padding: 0px 0px 0px 0px;
  flex-wrap: wrap;
`;

export const NewFolder = () => {
  const { t } = useTranslate();
  const router = useRouter();

  const parentFolderId = router.query.parentFolderId as string | undefined;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');

  const { createFolderWithConfig, isCreatingFolder } = useFolders();

  const handleReset = useCallback(() => {
    setFolderName('');
    setIsModalOpen(false);
  }, []);

  const handleCreateFolder = (name: string) => {
    createFolderWithConfig({
      folderConfig: {
        title: name,
        parentId: parentFolderId,
        isPrivate: true,
      },
      onSuccessFunction: handleReset,
    });
  };

  return (
    <>
      <BaseModal
        title={t('New folder')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
      >
        <Input
          placeholder={t('Name the folder')}
          value={folderName}
          disabled={isCreatingFolder}
          autoFocus
          maxLength={40}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFolderName(event.target.value)
          }
          onPressEnter={() => handleCreateFolder(folderName)}
        ></Input>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '15px',
          }}
        >
          <Button
            type="tertiary"
            styles={{
              border: 'none',
              color: theme.colors.black,
              boxShadow: 'none',
              marginRight: '5px',
            }}
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            <T keyName="Cancel" />
          </Button>
          <Button
            disabled={isCreatingFolder || folderName.length === 0}
            onClick={() => handleCreateFolder(folderName)}
          >
            {isCreatingFolder ? (
              <>
                <LoadingOutlined /> <T keyName="Creating folder" />
              </>
            ) : (
              <T keyName="Create folder" />
            )}
          </Button>
        </div>
      </BaseModal>
      <Button
        height={'42px'}
        type="ghost"
        styles={{
          // color: theme.colors.black,
          // backgroundColor: 'white',
          paddingLeft: 16,
          paddingRight: 16,
          boxShadow: theme.effects.floatingCreatorElementShadow,
        }}
        onClick={() => {
          setIsModalOpen(true);
        }}
        loading={isCreatingFolder}
      >
        + <T keyName="New folder" />
      </Button>
    </>
  );
};
export const NewWorkspaceFolder = () => {
  const { t } = useTranslate();
  const router = useRouter();

  const parentFolderId = router.query.parentFolderId as string | undefined;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');

  const { createFolderWithConfig, isCreatingFolder } = useWorkspaceFolders();

  const handleReset = useCallback(() => {
    setFolderName('');
    setIsModalOpen(false);
  }, []);

  const handleCreateFolder = (name: string) => {
    createFolderWithConfig({
      folderConfig: {
        title: name,
        parentId: parentFolderId,
        isPrivate: false,
      },
      onSuccessFunction: handleReset,
    });
  };

  return (
    <>
      <BaseModal
        title={t('New folder', 'New folder')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
      >
        <Input
          placeholder={t('Name the folder', 'Name the folder')}
          value={folderName}
          disabled={isCreatingFolder}
          autoFocus
          maxLength={40}
          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFolderName(event.target.value)
          }
          onPressEnter={() => handleCreateFolder(folderName)}
        ></Input>
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '15px',
          }}
        >
          <Button
            type="tertiary"
            styles={{
              border: 'none',
              color: theme.colors.black,
              boxShadow: 'none',
              marginRight: '5px',
            }}
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            <T keyName="Cancel" />
          </Button>
          <Button
            disabled={isCreatingFolder || folderName.length === 0}
            onClick={() => handleCreateFolder(folderName)}
          >
            {isCreatingFolder ? (
              <>
                <LoadingOutlined />{' '}
                <T keyName="Creating folder" defaultValue="Creating folder" />
              </>
            ) : (
              <T keyName="Create folder" defaultValue="Create folder" />
            )}
          </Button>
        </div>
      </BaseModal>
      <Button
        height={'42px'}
        type="ghost"
        styles={{
          // color: theme.colors.black,
          // backgroundColor: 'white',
          paddingLeft: 16,
          paddingRight: 16,
          boxShadow: theme.effects.floatingCreatorElementShadow,
        }}
        onClick={() => {
          setIsModalOpen(true);
        }}
        loading={isCreatingFolder}
      >
        <T keyName="+ New folder" defaultValue="+ New folder" />
      </Button>
    </>
  );
};
